/**
 * Query and mutation hooks over the six user-management routes (P19–P24).
 *
 * The list follows `usePaginatedQuery`'s contract — `page`/`pageSize` in the
 * URL, filters merged into the query — so "the invited users on page 2" is a
 * link, exactly like the estate and the jobs list.
 *
 * **Every write holds its `Idempotency-Key` in a ref** (§05.6, the
 * `ExportsPage.tsx` pattern): minted once per user gesture, reused across
 * retries of that gesture, cleared on success so the next gesture is a
 * genuinely new operation. One refinement over the plain ref: the key is bound
 * to a fingerprint of the payload, because §05.6's subtle clause is that a
 * *changed* body under a reused key is a 409 — a failed "invite asha@…"
 * followed by an edited "invite arun@…" is a new gesture, not a retry.
 */

import type { CreateUserRequest, UpdateUserRequest, UserDto } from '@rd/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { apiRequest, newIdempotencyKey } from '../../lib/apiClient.js';
import { usePaginatedQuery, type PaginatedQueryResult } from '../../lib/paginated.js';

export const USERS_KEY = ['users'] as const;

export interface UserListFilters {
  status?: string | undefined;
  search?: string | undefined;
}

/**
 * `POST /users` echoes the created user plus — outside production only — the
 * invite token, so the flow is exercisable before an email transport exists
 * (`users.controller.ts`). Optional by construction: production simply omits
 * it, and the UI must treat absence as the normal case.
 */
export type CreatedUser = UserDto & { developmentInviteToken?: string };

export interface ResendInviteResult {
  sent: boolean;
  developmentInviteToken?: string;
}

/**
 * One idempotency key per *gesture*, where a gesture is identified by its
 * payload. Same payload retried → same key (a request the server already
 * accepted must not run twice); edited payload → new key (a changed body under
 * a reused key is a 409 the server is right to give us); success → cleared, so
 * a deliberate repeat is a new operation.
 */
export function useGestureKey(): { keyFor(fingerprint: string): string; clear(): void } {
  const ref = useRef<{ fingerprint: string; key: string } | null>(null);
  return {
    keyFor(fingerprint: string): string {
      if (ref.current === null || ref.current.fingerprint !== fingerprint) {
        ref.current = { fingerprint, key: newIdempotencyKey() };
      }
      return ref.current.key;
    },
    clear(): void {
      ref.current = null;
    },
  };
}

export function useUsers(filters: UserListFilters): PaginatedQueryResult<UserDto> {
  return usePaginatedQuery<UserDto>({
    key: USERS_KEY,
    path: '/users',
    // Only filters the user actually set are sent — `status=` is a validation
    // failure at the API, not "any status" (same rule as the jobs list).
    query: {
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const gesture = useGestureKey();

  return useMutation<CreatedUser, unknown, CreateUserRequest>({
    mutationFn: (body) =>
      apiRequest<CreatedUser>('/users', {
        method: 'POST',
        body,
        idempotencyKey: gesture.keyFor(JSON.stringify(body)),
      }),
    onSuccess: () => {
      gesture.clear();
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const gesture = useGestureKey();

  return useMutation<UserDto, unknown, { userRef: string; changes: UpdateUserRequest }>({
    mutationFn: ({ userRef, changes }) =>
      apiRequest<UserDto>(`/users/${userRef}`, {
        method: 'PATCH',
        body: changes,
        idempotencyKey: gesture.keyFor(JSON.stringify({ userRef, changes })),
      }),
    onSuccess: () => {
      gesture.clear();
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useResendInvite() {
  const gesture = useGestureKey();

  return useMutation<ResendInviteResult, unknown, string>({
    mutationFn: (userRef) =>
      apiRequest<ResendInviteResult>(`/users/${userRef}/resend-invite`, {
        method: 'POST',
        idempotencyKey: gesture.keyFor(userRef),
      }),
    onSuccess: () => gesture.clear(),
  });
}

export function useForceLogout() {
  const gesture = useGestureKey();

  return useMutation<{ loggedOut: boolean }, unknown, string>({
    mutationFn: (userRef) =>
      apiRequest<{ loggedOut: boolean }>(`/users/${userRef}/force-logout`, {
        method: 'POST',
        idempotencyKey: gesture.keyFor(userRef),
      }),
    onSuccess: () => gesture.clear(),
  });
}
